import React, { useState, useEffect, useMemo } from "react";
import "./styles/metas.scss";
import {
  Container,
  Tab,
} from "react-bootstrap";
import { MetaHeader, TabNavigation, DashboardTab } from "./components";
import { TargetManagement } from "./TargetManagement";
import { Target, PeriodType, SalesData } from "./types";
import { useRankingPerformance, useMetas } from "../../../hooks/useMeta";
import { RankingPerformanceDto, MetaResumida } from "../../../types/meta";

type TabType = "dashboard" | "management";

// Estilos customizados para as abas
const tabStyles = `
  .clean-tabs .nav-link {
    border: none !important;
    background: transparent !important;
    color: var(--phoenix-body-color) !important;
    font-weight: 500;
    font-size: 15px;
    padding: 12px 24px;
    border-radius: 8px;
    transition: all 0.2s ease;
    position: relative;
    margin: 0 4px;
  }
  
  .clean-tabs .nav-link:hover {
    color: var(--phoenix-body-emphasis-color) !important;
    background: var(--phoenix-body-secondary-bg) !important;
  }
  
  .clean-tabs .nav-link.active {
    color: var(--phoenix-primary) !important;
    background: var(--phoenix-primary-bg-subtle) !important;
    font-weight: 600;
  }
  
  .clean-tabs .nav-link.active::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 24px;
    height: 2px;
    background: var(--phoenix-primary);
    border-radius: 1px;
  }
  
  .tab-content-wrapper {
    animation: fadeIn 0.3s ease-in-out;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

// Adicionar estilos ao head
if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = tabStyles;
  document.head.appendChild(styleElement);
}

// Breadcrumb items
const metaBreadcrumbItems = [
  { label: "Gerencial", url: "/gerencial" },
  { label: "Metas", url: "/gerencial/meta", active: true },
];

// Função para converter RankingPerformanceDto para SalesData
const convertToSalesData = (ranking: RankingPerformanceDto[]): SalesData[] => {
  return ranking.map((item, index) => ({
    id: item.usuarioId.toString(),
    name: item.nomeUsuario,
    position: "Vendedor", // Pode ser melhorado futuramente
    target: item.valorMeta,
    achieved: item.valorVendido,
    avatar: item.imageUrl ?? "",
    rank: index + 1,
  }));
};

export default function Meta() {
  const [activeTab, setActiveTab] = useState<TabType>("dashboard");
  const [selectedMeta, setSelectedMeta] = useState<MetaResumida | null>(null);

  // Buscar metas ativas para o dropdown
  const {
    data: metasResult,
    isLoading: isLoadingMetas,
    error: metasError
  } = useMetas({ status: "active" }, { enabled: activeTab === "dashboard" });

  const metasAtivas = metasResult?.data || [];

  // Selecionar meta padrão (meta ativa com maior quantidade de usuários)
  const metaPadrao = useMemo(() => {
    if (metasAtivas.length === 0) return null;

    return metasAtivas.reduce((metaComMaisUsuarios, metaAtual) => {
      if (!metaComMaisUsuarios) return metaAtual;
      return metaAtual.usuarios.length > metaComMaisUsuarios.usuarios.length
        ? metaAtual
        : metaComMaisUsuarios;
    });
  }, [metasAtivas]);

  // Definir meta selecionada (usar padrão se não houver seleção)
  useEffect(() => {
    if (metaPadrao && !selectedMeta) {
      setSelectedMeta(metaPadrao);
    }
  }, [metaPadrao, selectedMeta]);

  // Extrair datas da meta selecionada
  const { startDate, endDate } = useMemo(() => {
    if (!selectedMeta || !selectedMeta.dataInicio || !selectedMeta.dataFim) {
      return { startDate: "", endDate: "" };
    }

    return {
      startDate: selectedMeta.dataInicio.split("T")[0],
      endDate: selectedMeta.dataFim.split("T")[0]
    };
  }, [selectedMeta]);

  // Hook para buscar dados reais de ranking (apenas no dashboard e se há meta selecionada)
  const {
    data: rankingResult,
    isLoading: loading,
    error: queryError,
    refetch: refresh
  } = useRankingPerformance(startDate, endDate, {
    enabled: activeTab === "dashboard" && !!selectedMeta && !!startDate && !!endDate
  });

  // Extrair dados e erro do resultado
  const ranking = rankingResult?.isSuccess ? rankingResult.data || [] : [];
  const error = rankingResult?.isSuccess === false ? rankingResult.message :
    queryError ? (queryError as any)?.message || 'Erro ao carregar dados' : null;

  // Converter dados do ranking para o formato do SalesData
  const salesData = useMemo(() => convertToSalesData(ranking), [ranking]);

  // Função para alterar meta selecionada
  const handleMetaChange = (meta: MetaResumida) => {
    setSelectedMeta(meta);
  };

  return (
    <Container fluid className="px-0">
      {/* Header */}
      <MetaHeader breadcrumbItems={metaBreadcrumbItems} />

      <Container>
        {/* Navegação por Abas */}
        <Tab.Container
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k as TabType)}
        >
          <TabNavigation
            activeTab={activeTab}
            onTabChange={(tab) => setActiveTab(tab)}
          />

          <Tab.Content className="tab-content-wrapper">
            <Tab.Pane eventKey="dashboard">
              <DashboardTab
                selectedMeta={selectedMeta}
                metasDisponiveis={metasAtivas}
                salesData={salesData}
                loading={loading || isLoadingMetas}
                error={error || (metasError ? 'Erro ao carregar metas' : null)}
                onMetaChange={handleMetaChange}
                onRefresh={refresh}
              />
            </Tab.Pane>

            <Tab.Pane eventKey="management">
              {activeTab === "management" && (
                <TargetManagement
                  targets={[]}
                  onTargetsChange={() => { }}
                  isActive={true}
                />
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Container>
    </Container>
  );
};
